import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './CollegeDashboard.css';
import Jobs from './Jobs';
import Profile from './Profile';
import StudentsPage from './StudentsPage';
import StudentProfileViewer from './StudentProfileViewer';
import JobApplicants from './JobApplicants';
import CollegeCalendar from './CollegeCalendar';
import {
  Users,
  Briefcase,
  Building2,
  Bell,
  CalendarDays,
  LogOut,
  PanelLeftClose,
  PanelLeftOpen,
  GraduationCap,
} from 'lucide-react';

const NAV_ITEMS = [
  { key: 'Students', label: 'Students', icon: Users },
  { key: 'Jobs', label: 'Jobs', icon: Briefcase },
  { key: 'Profile', label: 'Profile', icon: Building2 },
  { key: 'Notice', label: 'Notice', icon: Bell },
  { key: 'Calendar', label: 'Calendar', icon: CalendarDays },
];

const CollegeDashboard = () => {
  const [activeTab, setActiveTab] = useState('Students');
  const [collegeName, setCollegeName] = useState('');
  const [collegeId, setCollegeId] = useState('');
  const [viewingStudentId, setViewingStudentId] = useState(null);
  const [viewingJobApplicants, setViewingJobApplicants] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    const storedCollegeName = localStorage.getItem('collegeName');
    const storedUserId = localStorage.getItem('userId');
    if (storedCollegeName) setCollegeName(storedCollegeName);
    if (storedUserId) setCollegeId(storedUserId);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('userId');
    localStorage.removeItem('collegeName');
    alert('Logged out successfully!');
    navigate('/');
  };

  const handleViewStudent = (studentId) => setViewingStudentId(studentId);

  const handleBack = () => {
    setViewingStudentId(null);
    setViewingJobApplicants(null);
  };

  const handleNavClick = (key) => {
    setActiveTab(key);
    handleBack();
  };

  const renderContent = () => {
    if (viewingStudentId) {
      return <StudentProfileViewer studentId={viewingStudentId} onBack={handleBack} />;
    }

    if (viewingJobApplicants) {
      return (
        <JobApplicants
          jobId={viewingJobApplicants.jobId}
          jobRole={viewingJobApplicants.jobRole}
          collegeUserId={collegeId}
          onBack={handleBack}
          onViewProfile={handleViewStudent}
        />
      );
    }

    switch (activeTab) {
      case 'Jobs':
        return <Jobs onViewApplicants={setViewingJobApplicants} />;
      case 'Students':
        return <StudentsPage collegeId={collegeId} onViewProfile={handleViewStudent} />;
      case 'Profile':
        return <Profile />;
      case 'Notice':
        return (
          <div className="cd-placeholder">
            <Bell size={40} className="cd-placeholder-icon" />
            <h2>Notices</h2>
            <p>Your announcements and notices will appear here.</p>
          </div>
        );
      case 'Calendar':
        return <CollegeCalendar onSwitchToJobs={() => setActiveTab('Jobs')} />;
      default:
        return null;
    }
  };

  const getInitials = (name) => {
    if (!name) return 'C';
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return parts[0][0].toUpperCase();
  };

  return (
    <div className="cd-layout">
      {/* ═══ Sidebar ═══ */}
      <aside className={`cd-sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>

        {/* Sidebar Top — College Branding + Collapse Toggle */}
        <div className="cd-sidebar-top">
          <div className="cd-sidebar-brand">
            <div className="cd-brand-icon">
              <GraduationCap size={22} />
            </div>
            {!sidebarCollapsed && (
              <div className="cd-brand-text">
                <span className="cd-brand-name">
                  {collegeName || 'College Portal'}
                </span>
                <span className="cd-brand-sub">Admin Dashboard</span>
              </div>
            )}
          </div>

          {/* Collapse Toggle — INSIDE the sidebar */}
          <button
            className="cd-collapse-btn"
            onClick={() => setSidebarCollapsed((c) => !c)}
            title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {sidebarCollapsed
              ? <PanelLeftOpen size={18} />
              : <PanelLeftClose size={18} />
            }
          </button>
        </div>

        {/* Navigation */}
        <nav className="cd-sidebar-nav">
          {NAV_ITEMS.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              className={`cd-nav-item ${activeTab === key ? 'active' : ''}`}
              onClick={() => handleNavClick(key)}
              title={sidebarCollapsed ? label : ''}
            >
              <Icon size={20} className="cd-nav-icon" />
              {!sidebarCollapsed && <span className="cd-nav-label">{label}</span>}
              {!sidebarCollapsed && activeTab === key && (
                <span className="cd-nav-indicator" />
              )}
            </button>
          ))}
        </nav>

        {/* Sidebar Footer */}
        <div className="cd-sidebar-footer">
          {!sidebarCollapsed && (
            <div className="cd-user-card" onClick={() => handleNavClick('Profile')}>
              <div className="cd-user-avatar">{getInitials(collegeName)}</div>
              <div className="cd-user-info">
                <span className="cd-user-name">
                  {collegeName
                    ? collegeName.length > 18
                      ? collegeName.substring(0, 18) + '…'
                      : collegeName
                    : 'College'}
                </span>
                <span className="cd-user-role">Administrator</span>
              </div>
            </div>
          )}

          <button className="cd-logout-btn" onClick={handleLogout} title="Log out">
            <LogOut size={18} />
            {!sidebarCollapsed && <span>Log Out</span>}
          </button>
        </div>
      </aside>

      {/* ═══ Main Content ═══ */}
      <main className="cd-main">
        <div className="cd-content">{renderContent()}</div>
      </main>
    </div>
  );
};

export default CollegeDashboard;